/**
 * Where a level's end leads (spec 02 §9): the levels the screen scrolls through on the way, at the
 * C64's pace of one level per 32 frames - one for a normal clear, each skipped level for an umbrella
 * or the white door (50 -> 70 scrolls 51..69 past, about 12 s), the secret room behind a red door and
 * back out two levels on - or the ending.
 */

import { level } from '../../levels';
import { SECRET_ROOM } from '../../levels/secretRoom';
import type { LevelDef } from '../../levels/types';
import type { LevelExit } from '../../sim/types';

export type ExitPlan =
  | { kind: 'scroll'; path: LevelDef[]; /** The red door's level when the path ends in the secret room, else -1. */ secretFrom: number }
  | { kind: 'ending' };

const LAST = 100;

/** Every level after `from` up to and including `to` (at least the next one), capped at 100. */
function levelsBetween(from: number, to: number): LevelDef[] {
  const end = Math.min(LAST, Math.max(from + 1, to));
  const out: LevelDef[] = [];
  for (let n = from + 1; n <= end; n++) out.push(level(n));
  return out;
}

/** The plan for leaving level `n` (0: the secret room) by `exit`. */
export function planExit(n: number, exit: LevelExit): ExitPlan {
  switch (exit.kind) {
    case 'ending':
      return { kind: 'ending' };
    case 'secret':
      return { kind: 'scroll', path: [SECRET_ROOM], secretFrom: exit.from };
    case 'resume':
      return { kind: 'scroll', path: [level(Math.min(LAST, exit.to))], secretFrom: -1 };
    case 'skip':
    case 'warp':
      return { kind: 'scroll', path: levelsBetween(n, exit.to), secretFrom: -1 };
    case 'next':
      // Level 100 ends only by the win; guard anyway.
      return n >= LAST ? { kind: 'ending' } : { kind: 'scroll', path: [level(n + 1)], secretFrom: -1 };
  }
}
